import React, { useMemo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { LinearProgress } from '@material-ui/core';
import { isArray } from 'lodash';
import moment from 'moment';
import qs from 'querystring';

import { t } from '~/helpers';
import api from '~/services/api';

import { Header, Table } from './components';
import * as S from './styles';

const FIELD_LIST =
  'movimentacaopitstopextratoareia,datahorapitstopextratoareia,' +
  'nomeusuariopitstopextratoareia,entradapitstopextratoareia,saidapitstopextratoareia,' +
  'saldopitstopextratoareia';

function RelPitStopExtratoAreia() {
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [textoPesquisa, setTextoPesquisa] = useState('');
  const [lastReportParams, setLastReportParams] = useState(null);

  const header = useMemo(() => {
    const agora = moment();

    return {
      datainicio: agora
        .clone()
        .subtract(7, 'day')
        .format('DD/MM/YYYY 00:00:00'),
      datafim: agora.clone().format('DD/MM/YYYY 23:59:59'),
      idposto: '',
      codigonomeposto: '',
    };
  }, []);

  const validaCamposObrigatorios = useCallback((formData) => {
    if (formData.datainicio && formData.datafim) {
      if (formData.datainicio.length != 19 || formData.datafim.length != 19) {
        toast.error(t('s.cadastro.pitstop.periodo-deve-ser-informado'));
        return false;
      }
    } else {
      toast.error(t('s.cadastro.pitstop.periodo-deve-ser-informado'));
      return false;
    }

    if (!formData.idposto || Number(formData.idposto) <= 0) {
      toast.error(t('s.operacao.descarganf.postoobrigatorio'));
      return false;
    }

    return true;
  }, []);

  const getFiltroPesquisa = useCallback((formData) => {
    let filtro =
      `FILTRO - ${t('s.geral.opcoes.datahorainicial')}: ${
        formData.datainicio
      } | ` + `${t('s.geral.opcoes.datahorafinal')}: ${formData.datafim}`;

    if (formData.codigonomeposto) {
      filtro += ` | ${t('s.geral.postocomboio')}: ${formData.codigonomeposto}`;
    }

    return filtro;
  }, []);

  const getParams = useCallback(
    (formData) => ({
      dataInicio: moment(formData.datainicio, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DD HH:mm:ss'
      ),
      dataFim: moment(formData.datafim, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DD HH:mm:ss'
      ),
      idposto: Number(formData.idposto),
    }),
    []
  );

  const handleSubmitHeader = useCallback(
    async (formData) => {
      if (!validaCamposObrigatorios(formData)) {
        return;
      }

      try {
        setLoading(true);

        const params = getParams(formData);
        const filtro = getFiltroPesquisa(formData);

        setLastReportParams(params);
        setTextoPesquisa(filtro);

        const { data } = await api.get('pitstop/relatorio/extratoareia', {
          params,
          paramsSerializer: (param) => qs.stringify(param),
        });

        setDataTable(data);
      } catch (err) {
        if (isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [getFiltroPesquisa, getParams, validaCamposObrigatorios]
  );

  const handleExportData = useCallback(
    async (formato) => {
      try {
        setLoading(true);

        if (!lastReportParams) {
          return;
        }

        const tituloRelatorio = t('s.menu.relatorio.relpitstopextratoareia');
        const reportRequestParams = {
          fieldlist: FIELD_LIST,
          relatorio: {
            custom: 'true',
            formato,
            tituloRelatorio,
            textoPesquisa,
            substituirHeader: JSON.stringify({
              movimentacaopitstopextratoareia: {
                label: t('s.relatorio.resumoturno.movimentacao'),
                align: 'left',
              },
              datahorapitstopextratoareia: {
                label: t('s.operacao.turno.datahora'),
                align: 'center',
              },
              nomeusuariopitstopextratoareia: {
                label: t('s.cadastro.pitstop.nomeusuarioresponsavel'),
                align: 'left',
              },
              entradapitstopextratoareia: {
                label: t('s.relatorio.resumoturno.entrada'),
                align: 'right',
              },
              saidapitstopextratoareia: {
                label: t('s.relatorio.resumoturno.saida'),
                align: 'right',
              },
              saldopitstopextratoareia: {
                label: t('s.relatorio.extratoproduto.saldo'),
                align: 'right',
              },
            }),
          },
        };

        const response = await api.post(
          'pitstop/relatorio/extratoareia/export',
          reportRequestParams,
          {
            params: lastReportParams,
            paramsSerializer: (param) => qs.stringify(param),
            responseType: 'blob',
          }
        );

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;

        if (formato.toLowerCase() === 'excel') {
          link.download = `${tituloRelatorio}.xls`;
        } else {
          link.target = '_blank';
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        if (Array.isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [lastReportParams, textoPesquisa]
  );

  return (
    <S.Wrapper>
      {loading && <LinearProgress />}
      <Header
        data={header}
        loading={loading}
        handleSubmit={handleSubmitHeader}
        canExport={dataTable.length > 0}
        onExportClick={handleExportData}
      />

      <Table rows={dataTable} />
    </S.Wrapper>
  );
}

export default RelPitStopExtratoAreia;
